import { type MutableRefObject } from 'react';
import { Sparkles } from 'lucide-react';
import { ChatMessage, type ChatMessageItem } from '../chat/ChatMessage';
import { CardLanguageProvider } from '../chat/order-card/CardLanguage';
import { type SuggestedAction } from '../../types/ai';
import type { AdminFeedbackType } from '../../types/feedback';
import { type ChannelValue, type StreamStatus } from '../../pages/playground/constants';

type PlaygroundMessageListProps = {
  scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
  onMessageScroll: () => void;
  messages: ChatMessageItem[];
  sessionId: string;
  language: string;
  channel: ChannelValue;
  onSuggestedActionClick: (action: SuggestedAction) => void;
  onAdminFeedback: (type: AdminFeedbackType, assistantMsg: ChatMessageItem) => void;
  onEmailDraftResolved: (
    resolvedMessage: string,
    status: 'confirmed' | 'already_sent' | 'cancelled',
    sessionClosed?: boolean,
  ) => void;
  isStreaming: boolean;
  showStreamDebug: boolean;
  streamStatusLines: string[];
  activeStreamStatus: StreamStatus | null;
};

export const PlaygroundMessageList = ({
  scrollContainerRef,
  onMessageScroll,
  messages,
  sessionId,
  language,
  channel,
  onSuggestedActionClick,
  onAdminFeedback,
  onEmailDraftResolved,
  isStreaming,
  showStreamDebug,
  streamStatusLines,
  activeStreamStatus,
}: PlaygroundMessageListProps) => (
  <div
    ref={scrollContainerRef}
    onScroll={onMessageScroll}
    className="flex-1 space-y-5 overflow-y-auto bg-brand-beige-tint-1 px-5 py-5"
  >
    {messages.length === 0 ? (
      <div className="flex h-full flex-col items-center justify-center rounded-sm border border-dashed border-brand-blue/20 bg-white/70 p-8 text-center text-text-grey">
        <Sparkles className="mb-3 h-8 w-8 text-brand-blue/40" />
        <p className="text-sm font-medium text-brand-blue">Inga meddelanden ännu.</p>
        <p className="mt-2 text-sm text-text-grey">
          Ställ en fråga till agenten för att komma igång.
        </p>
      </div>
    ) : (
      <CardLanguageProvider language={language}>
        {(() => {
          let lastAssistantIdx = -1;
          for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === 'assistant') {
              lastAssistantIdx = i;
              break;
            }
          }
          return messages.map((chatMessage, index) => {
            const isLatest = chatMessage.role === 'assistant' && index === lastAssistantIdx;
            return (
              <ChatMessage
                key={chatMessage.id}
                message={chatMessage}
                isLatestAssistant={isLatest}
                onSuggestedActionClick={isLatest ? onSuggestedActionClick : undefined}
                sessionId={sessionId}
                channel={channel === 'playground' ? 'website' : channel}
                showAdminFeedback={chatMessage.role === 'assistant'}
                onAdminFeedback={
                  chatMessage.role === 'assistant'
                    ? (type) => onAdminFeedback(type, chatMessage)
                    : undefined
                }
                onEmailDraftResolved={onEmailDraftResolved}
              />
            );
          });
        })()}
      </CardLanguageProvider>
    )}

    {isStreaming || (showStreamDebug && streamStatusLines.length > 0) ? (
      <div className="flex items-start gap-2">
        <div className="rounded-2xl rounded-bl-sm border border-black/10 bg-white px-4 py-3 shadow-sm">
          <div className="flex min-h-[3.5rem] flex-col justify-center gap-2">
            {activeStreamStatus ? (
              <p
                aria-live="polite"
                className="max-w-[12rem] text-[11px] font-medium leading-4 text-text-grey"
              >
                {activeStreamStatus.text}
              </p>
            ) : null}
            <div className="flex h-4 items-center gap-1" aria-label="Assistenten skriver">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-blue/45 [animation-delay:-0.24s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-blue/60 [animation-delay:-0.12s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-blue/75" />
            </div>
          </div>
        </div>
        {showStreamDebug && streamStatusLines.length > 0 ? (
          <div className="max-w-sm pt-6 text-[11px] leading-5 text-text-grey/70">
            {streamStatusLines.slice(-2).map((line, index) => (
              <p key={`${line}-${index}`}>{line}</p>
            ))}
          </div>
        ) : null}
      </div>
    ) : null}
  </div>
);
